import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
    Users, Calendar, Trophy, Flame, Award, BookOpen, ChevronRight,
    Loader2, Sparkles, Clock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import IntrospectionForm from "@/components/IntrospectionForm";
import BadgePill from "@/components/BadgePill";
import ShareButton from "@/components/ShareButton";

function formatDate(iso, locale) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso.slice(0, 10);
    }
}

function diffHours(iso) {
    return (new Date(iso).getTime() - Date.now()) / 3_600_000;
}

export default function ClubDetail() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        try {
            const [{ data: detail }, { data: lb }] = await Promise.all([
                api.get(`/clubs/${slug}`),
                api.get(`/clubs/${slug}/leaderboard`),
            ]);
            setData(detail);
            setLeaderboard(lb);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            navigate("/clubs");
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [slug, user?.id]);

    if (!data) {
        return (
            <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 py-24">
                <p className="font-display text-2xl italic text-[var(--ink)] animate-slow-pulse">…</p>
            </main>
        );
    }

    const { club, current_cycle: cycle, membership, can_enrol, has_submitted } = data;

    const handleJoin = async () => {
        if (!user || typeof user !== "object") {
            toast.error(t("clubs.login_required"));
            navigate("/acceder");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/clubs/${slug}/join`);
            toast.success(t("clubs.joined", { name: club.name }));
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const handleNewCycle = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/clubs/${slug}/cycles/generate`);
            toast.success(t("clubs.cycle_started", { n: r.data.cycle_number }));
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const handleIntroSubmit = async (payload) => {
        try {
            const r = await api.post(`/clubs/${slug}/cycles/${cycle.id}/introspection`, payload);
            toast.success(t("clubs.intro_xp", { xp: r.data.xp_gained }));
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const cycleClosed = cycle && diffHours(cycle.deadline_at) < 0;
    const enrolHoursLeft = cycle ? Math.max(0, Math.floor(diffHours(cycle.enrollment_closes_at))) : 0;

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <Link to="/clubs" className="text-xs uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors mb-4 inline-block">
                    ← {t("clubs.back_to_list")}
                </Link>
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t(`clubs.kind_${club.kind}`)} · {club.language?.toUpperCase()}
                </p>
                <h1
                    className="font-display text-5xl md:text-7xl text-[var(--ink)] tracking-tight mb-3"
                    style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}
                    data-testid="club-name"
                >
                    {club.name}
                </h1>
                <p className="text-[var(--ink-muted)] text-lg max-w-3xl">{club.theme}</p>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex flex-wrap items-center gap-6 text-sm text-[var(--ink-dim)]">
                        <span className="inline-flex items-center gap-2"><Users size={14} strokeWidth={1.5} /> {club.member_count} {t("clubs.members")}</span>
                        <span className="inline-flex items-center gap-2"><BookOpen size={14} strokeWidth={1.5} /> {club.cycle_count} {t("clubs.cycles")}</span>
                    </div>
                    <ShareButton
                        recId={club.slug}
                        url={`${window.location.origin}/clubs/${club.slug}`}
                        text={t("clubs.share_invite", { name: club.name })}
                        label={t("clubs.invite_friends")}
                        testIdPrefix="club-share"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* CYCLE */}
                <section className="lg:col-span-2 space-y-10">
                    {cycle ? (
                        <article className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-6 md:p-8">
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
                                    {t("clubs.cycle_n", { n: cycle.cycle_number })} · {cycleClosed ? t("clubs.status_closed") : t("clubs.status_active")}
                                </p>
                                {!cycleClosed && (
                                    <span className="inline-flex items-center gap-1 text-xs text-[var(--ink-dim)]">
                                        <Calendar size={12} strokeWidth={1.5} /> {formatDate(cycle.deadline_at, i18n.language)}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-6">
                                <div className="w-32 sm:w-36 flex-shrink-0 aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border)]">
                                    {cycle.book?.cover_url ? (
                                        <img src={cycle.book.cover_url} alt={cycle.book.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[var(--ink-dim)]">
                                            <BookOpen size={28} strokeWidth={1} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-display text-3xl md:text-4xl text-[var(--ink)] leading-tight mb-2"
                                        style={{ fontVariationSettings: '"wght" 600, "wdth" 88, "opsz" 64' }}
                                        data-testid="cycle-book-title">
                                        {cycle.book.title}
                                    </h2>
                                    <p className="text-sm text-[var(--ink-muted)] mb-3">{cycle.book.author} · {cycle.book.year || "—"} · {cycle.book.pages || "?"} {t("clubs.pages")}</p>
                                    <p className="text-sm text-[var(--ink-muted)] leading-relaxed mb-4">{cycle.book.synopsis}</p>
                                    {cycle.book.why_chosen && (
                                        <p className="text-xs text-[var(--accent)] italic mb-2">★ {cycle.book.why_chosen}</p>
                                    )}
                                </div>
                            </div>

                            {/* Action zone */}
                            <div className="mt-8 pt-6 border-t border-[var(--border)]">
                                {!membership && can_enrol && (
                                    <div className="flex flex-wrap items-center gap-4">
                                        <button
                                            data-testid="club-join-btn"
                                            onClick={handleJoin}
                                            disabled={busy}
                                            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-6 py-2.5 text-sm uppercase tracking-widest"
                                        >
                                            {busy && <Loader2 size={14} className="animate-spin" />}
                                            {t("clubs.join_cta")}
                                        </button>
                                        <span className="text-xs text-[var(--ink-dim)] inline-flex items-center gap-1">
                                            <Clock size={12} strokeWidth={1.5} />
                                            {t("clubs.enrol_closes_in_h", { h: enrolHoursLeft })}
                                        </span>
                                    </div>
                                )}
                                {!membership && !can_enrol && (
                                    <p className="text-sm text-[var(--ink-dim)]">{t("clubs.enrolment_locked")}</p>
                                )}
                                {membership && !cycleClosed && !has_submitted && (
                                    <IntrospectionForm onSubmit={handleIntroSubmit} />
                                )}
                                {membership && has_submitted && !cycleClosed && (
                                    <p className="text-sm text-[var(--accent)] inline-flex items-center gap-2">
                                        <Sparkles size={14} strokeWidth={1.5} /> {t("clubs.intro_submitted")}
                                    </p>
                                )}
                                {cycleClosed && (
                                    <div className="flex flex-wrap items-center gap-4">
                                        <Link
                                            to={`/clubs/${slug}/cycles/${cycle.id}/summary`}
                                            data-testid="cycle-summary-link"
                                            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2.5 text-sm uppercase tracking-widest"
                                        >
                                            {t("clubs.read_summary")} <ChevronRight size={14} strokeWidth={1.8} />
                                        </Link>
                                        {(membership || club.kind === "community") && (
                                            <button
                                                data-testid="cycle-next-btn"
                                                onClick={handleNewCycle}
                                                disabled={busy}
                                                className="inline-flex items-center gap-2 border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] disabled:opacity-50 transition-colors px-5 py-2 text-xs uppercase tracking-widest"
                                            >
                                                {busy && <Loader2 size={12} className="animate-spin" />}
                                                {t("clubs.start_next_cycle")}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </article>
                    ) : (
                        <div className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-12 text-center">
                            <BookOpen size={36} strokeWidth={1} className="mx-auto text-[var(--accent)] mb-4" />
                            <p className="font-display text-2xl text-[var(--ink)] mb-3"
                               style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                                {t("clubs.no_cycle_yet")}
                            </p>
                            {(club.kind === "community" || membership) && (
                                <button
                                    data-testid="cycle-start-first"
                                    onClick={handleNewCycle}
                                    disabled={busy}
                                    className="mt-4 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-6 py-2.5 text-sm uppercase tracking-widest"
                                >
                                    {t("clubs.start_first_cycle")}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Membership stats */}
                    {membership && (
                        <article className="border border-[var(--border)] p-6 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="membership-stats">
                            <Stat icon={Trophy} label={t("clubs.xp")} value={membership.xp} />
                            <Stat icon={Flame} label={t("clubs.streak")} value={membership.streak} />
                            <Stat icon={BookOpen} label={t("clubs.completed")} value={membership.cycles_completed} />
                            <Stat icon={Award} label={t("clubs.badges")} value={membership.badges?.length || 0} />
                            {membership.badges?.length > 0 && (
                                <div className="col-span-2 sm:col-span-4 flex flex-wrap gap-2 pt-2">
                                    {membership.badges.map((b) => <BadgePill key={b} code={b} />)}
                                </div>
                            )}
                        </article>
                    )}
                </section>

                {/* LEADERBOARD */}
                <aside>
                    <h3 className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-4 inline-flex items-center gap-2">
                        <Trophy size={12} strokeWidth={1.8} /> {t("clubs.leaderboard")}
                    </h3>
                    {leaderboard.length === 0 ? (
                        <p className="text-sm text-[var(--ink-dim)]">{t("clubs.leaderboard_empty")}</p>
                    ) : (
                        <ol className="space-y-2" data-testid="leaderboard">
                            {leaderboard.map((row) => (
                                <li key={row.rank} className="flex items-center justify-between gap-3 border-b border-[var(--border)] py-2.5">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-[var(--ink-dim)] font-mono-num text-xs w-6 text-right">{row.rank.toString().padStart(2, "0")}</span>
                                        <span className="text-sm text-[var(--ink)] truncate">{row.name}</span>
                                        {row.streak >= 3 && <Flame size={12} className="text-[var(--accent)]" />}
                                    </div>
                                    <span className="text-xs text-[var(--ink-muted)] font-mono-num">{row.xp} XP</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </aside>
            </div>
        </main>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-1 inline-flex items-center gap-1.5">
                <Icon size={11} strokeWidth={1.5} /> {label}
            </p>
            <p className="font-display text-3xl text-[var(--ink)] font-mono-num"
               style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                {value ?? 0}
            </p>
        </div>
    );
}
